import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clock, Edit, Trash2, Plus, Bell, Check } from "lucide-react";
import { REMINDER_BEFORE_OPTIONS, RECURRENCE_OPTIONS } from "@/hooks/useReminders";

const formatReminderTime = (datetimeIso) => {
  if (!datetimeIso) return "";
  const date = new Date(datetimeIso);
  return date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
};

const formatReminderBefore = (minutes) => {
  const option = REMINDER_BEFORE_OPTIONS.find(o => o.value === minutes);
  return option ? option.label : `${minutes} min prima`;
};

const DayDetails = ({
  selectedDate,
  activeTab,
  onTabChange,
  appointments,
  reminders,
  loading,
  onEditAppointment,
  onDeleteAppointment,
  onAddAppointment,
  onEditReminder,
  onDeleteReminder,
  onCompleteReminder,
  onAddReminder
}) => {
  const formatSelectedDate = () => {
    return new Date(selectedDate).toLocaleDateString("it-IT", {
      weekday: "long",
      day: "numeric",
      month: "long"
    });
  };

  const selectedDayAppointments = appointments
    .filter(a => a.date === selectedDate)
    .sort((a, b) => {
      if (!a.time) return 1;
      if (!b.time) return -1;
      return a.time.localeCompare(b.time);
    });

  const selectedDayReminders = reminders
    .filter(r => r.datetime_iso && r.datetime_iso.startsWith(selectedDate))
    .sort((a, b) => a.datetime_iso.localeCompare(b.datetime_iso));

  return (
    <Card className="border border-border shadow-sm rounded-sm" data-testid="day-details">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-lg capitalize">
          {formatSelectedDate()}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={onTabChange}>
          <TabsList className="grid w-full grid-cols-2 rounded-sm">
            <TabsTrigger value="appointments" className="rounded-sm text-xs">
              Appuntamenti ({selectedDayAppointments.length})
            </TabsTrigger>
            <TabsTrigger value="reminders" className="rounded-sm text-xs">
              Promemoria ({selectedDayReminders.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="appointments" className="mt-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-foreground"></div>
              </div>
            ) : selectedDayAppointments.length > 0 ? (
              <ul className="space-y-3">
                {selectedDayAppointments.map(apt => (
                  <li 
                    key={apt.appointment_id} 
                    className="p-3 border border-border rounded-sm"
                    data-testid={`apt-item-${apt.appointment_id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-sm">{apt.title}</h4>
                        {apt.time && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" /> {apt.time}
                          </p>
                        )}
                        {apt.description && (
                          <p className="text-xs text-muted-foreground mt-2">{apt.description}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => onEditAppointment(apt)}
                          className="text-muted-foreground hover:text-foreground transition-colors p-1"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onDeleteAppointment(apt.appointment_id)}
                          className="text-muted-foreground hover:text-destructive transition-colors p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground mb-4">Nessun appuntamento</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => onAddAppointment(selectedDate)}
                  className="rounded-sm"
                >
                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="reminders" className="mt-4">
            {selectedDayReminders.length > 0 ? (
              <ul className="space-y-3">
                {selectedDayReminders.map(reminder => (
                  <li 
                    key={reminder.reminder_id} 
                    className={`p-3 border rounded-sm ${reminder.is_completed ? 'border-green-200 bg-green-50/50' : 'border-orange-200 bg-orange-50/50'}`}
                    data-testid={`reminder-item-${reminder.reminder_id}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className={`font-medium text-sm ${reminder.is_completed ? 'line-through text-muted-foreground' : ''}`}>
                          {reminder.title}
                        </h4>
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <Bell className="w-3 h-3" /> {formatReminderTime(reminder.datetime_iso)}
                          <span className="ml-2 px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-[10px]">
                            {formatReminderBefore(reminder.reminder_before)}
                          </span>
                        </p>
                        {reminder.is_recurring && (
                          <p className="text-[10px] text-orange-600 mt-1">
                            🔄 {RECURRENCE_OPTIONS.find(o => o.value === reminder.recurrence_type)?.label || 'Ricorrente'}
                          </p>
                        )}
                        {reminder.description && (
                          <p className="text-xs text-muted-foreground mt-2">{reminder.description}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {!reminder.is_completed && (
                          <button
                            onClick={() => onCompleteReminder(reminder.reminder_id)}
                            className="text-green-600 hover:text-green-700 transition-colors p-1"
                            title="Segna come completato"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => onEditReminder(reminder)}
                          className="text-muted-foreground hover:text-foreground transition-colors p-1"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onDeleteReminder(reminder.reminder_id)}
                          className="text-muted-foreground hover:text-destructive transition-colors p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground mb-4">Nessun promemoria</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => onAddReminder(selectedDate)}
                  className="rounded-sm"
                >
                  <Bell className="w-4 h-4 mr-1" /> Aggiungi
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default DayDetails;
